// eDOS · Test Detail page. Opens when a partner clicks any row in
// the eDOS results list (Tests / Departments / Speciality). Mirrors
// the v2.1 design spec: hero block, four-card test-information grid
// (Specifications / Applicability / Sample / Schedule), and a sticky
// right-rail with L2L pricing, key stats, action CTAs, and a
// sample-report card.
//
// Detail data is fetched live from the eDOS Laravel API via
// `useGetTestByCodeQuery(code)` and adapted to the rich render shape
// by `mapApiToEdosDetail`. Related tests come from a second query
// scoped to the same department (via `useGetTestsListQuery`).
import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { MP, MONO, DISPLAY } from '../medical/shared';
import { useGetTestByCodeQuery, useGetTestsListQuery } from '../../../store/edosApi';
import { mapApiToEdosDetail, mapApiToTestRow } from '../../../utils/edosLabAdapter';
import { useUI } from '../../../context/UIContext';
import EdosTestDetailShimmer from './EdosTestDetailShimmer';

// ---------- atoms ----------

// Pill used in the hero (CODE / DEPT / NABL).
const HeroPill = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: { bg: MP.white,    fg: MP.dark,     border: MP.border },
    nabl:    { bg: MP.greenLight, fg: MP.green,  border: MP.green },
  };
  const c = tones[tone] || tones.default;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.875,
        bgcolor: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: '6px',
        px: 1.25,
        py: 0.625,
        fontFamily: MONO,
        fontSize: 11,
      }}
    >
      <Box component="span" sx={{ color: MP.textMuted, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10 }}>
        {label}
      </Box>
      <Box component="span" sx={{ color: c.fg, fontWeight: 700, letterSpacing: '0.02em' }}>
        {value}
      </Box>
    </Box>
  );
};

// Yes/No badge — green YES, neutral NO. Matches the "TRUE/FALSE"
// affordance in the design spec for applicability flags.
const YesNoBadge = ({ value }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      bgcolor: value ? MP.greenLight : MP.bg,
      color: value ? MP.green : MP.textMuted,
      fontFamily: MONO,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '0.04em',
      px: 1,
      py: 0.25,
      borderRadius: '20px',
      border: `1px solid ${value ? MP.green : MP.border}`,
    }}
  >
    {value ? 'YES' : 'NO'}
  </Box>
);

// NABL accreditation chip — fills the Applicability "Accreditation"
// row.
const AccreditationBadge = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      bgcolor: MP.green,
      color: MP.white,
      fontFamily: MONO,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.08em',
      px: 1,
      py: 0.375,
      borderRadius: '6px',
    }}
  >
    {label}
  </Box>
);

// Gender chips — "Male" / "Female" / both, with MUI sex icons. The
// `gender` value is one of 'male' | 'female' | 'both'.
const GenderRow = ({ gender }) => {
  const showMale = gender === 'male' || gender === 'both';
  const showFemale = gender === 'female' || gender === 'both';
  const Chip = ({ icon: Icon, label, color, bg }) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: bg,
        border: `1px solid ${color}33`,
        color,
        fontFamily: MONO,
        fontSize: 11,
        fontWeight: 700,
        px: 0.875,
        py: 0.375,
        borderRadius: '6px',
      }}
    >
      <Icon sx={{ fontSize: 14 }} />
      {label}
    </Box>
  );
  return (
    <Stack direction="row" spacing={0.75}>
      {showMale && <Chip icon={MaleIcon} label="Male" color="#1976D2" bg="#E3F2FD" />}
      {showFemale && <Chip icon={FemaleIcon} label="Female" color="#C2185B" bg="#FCE4EC" />}
    </Stack>
  );
};

// Age-range chip with FamilyRestroom icon.
const AgeRangeChip = ({ ageRange }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      bgcolor: MP.tealLight,
      border: `1px solid ${MP.tealMid}`,
      color: MP.tealDark,
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 700,
      px: 0.875,
      py: 0.375,
      borderRadius: '6px',
    }}
  >
    <FamilyRestroomIcon sx={{ fontSize: 14 }} />
    {ageRange}
  </Box>
);

// Two-column key/value row used inside the four info cards.
const InfoRow = ({ label, children, last = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
      gap: { xs: 0.5, sm: 2 },
      alignItems: 'center',
      px: 2,
      py: 1.25,
      borderBottom: last ? 'none' : `1px dashed ${MP.border}`,
      minHeight: 44,
    }}
  >
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: MP.textMuted,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: 13, color: MP.dark, lineHeight: 1.4 }}>{children}</Box>
  </Box>
);

// One of the four info cards. Dark green header, white body, list of
// InfoRow children.
const InfoCard = ({ title, children }) => (
  <Box
    sx={{
      bgcolor: MP.white,
      border: `1px solid ${MP.border}`,
      borderRadius: '6px',
      boxShadow: MP.shadow,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        bgcolor: MP.dark,
        color: MP.white,
        px: 2,
        py: 1.25,
        fontFamily: MONO,
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </Box>
    <Box>{children}</Box>
  </Box>
);

// Sticky-aside stat row (TAT / Schedule / Sample / Fasting).
const AsideStat = ({ label, value, last = false }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      px: 2.25,
      py: 1.25,
      borderBottom: last ? 'none' : `1px solid ${MP.border}`,
    }}
  >
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: MP.textMuted,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: MP.dark }}>
      {value}
    </Typography>
  </Stack>
);

// ---------- main component ----------

const EdosTestDetail = ({ test, onBack, onSelectTest, sectionLabel = 'Search' }) => {
  const { showToast } = useUI();

  // Live detail fetch — keyed by test_code. Returns the full row from
  // /get-test-specification-by-code, which we adapt to the rich render
  // shape via mapApiToEdosDetail. RTK caches by code so flipping back
  // to a previously-viewed test is instant.
  const detailQuery = useGetTestByCodeQuery(test.code, { skip: !test?.code });
  const detail = useMemo(
    () => mapApiToEdosDetail(detailQuery.data),
    [detailQuery.data],
  );

  // Related tests — same department, capped at 5 (we slice to 4 after
  // dropping the current test). Skipped until detail resolves so we
  // know which department to query.
  const relatedQuery = useGetTestsListQuery(
    { department: detail?.dept, perPage: 5 },
    { skip: !detail?.dept },
  );
  const related = useMemo(() => {
    const rows = (relatedQuery.data?.items || [])
      .map(mapApiToTestRow)
      .filter(Boolean);
    return rows.filter((r) => r.code !== test.code).slice(0, 4);
  }, [relatedQuery.data, test.code]);

  // Loading state — full layout-matching shimmer so the page doesn't
  // jump when the API resolves. Mirrors the back link, crumbs, hero,
  // four-card grid, ICD-10, branch availability, related tests, and
  // sticky pricing aside.
  if (detailQuery.isLoading) {
    return <EdosTestDetailShimmer />;
  }

  // Error / not-found state
  if (detailQuery.isError || !detail) {
    return (
      <Stack spacing={2.5} sx={{ alignItems: 'center', py: 8 }}>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: MP.red }}>
          Test {test.code} not found
        </Typography>
        <Typography sx={{ fontSize: 13, color: MP.textMuted, maxWidth: '50ch', textAlign: 'center' }}>
          {detailQuery.error?.data?.message
            || 'The eDOS API returned no data for this code. The test may have been deactivated.'}
        </Typography>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: MP.tealDark,
            fontFamily: MONO,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Back to results
        </Button>
      </Stack>
    );
  }

  const handlePrint = () => window.print();
  const handleDownloadTrf = () =>
    showToast('TRF download wires to test_master.pdf in v2.2.', 'info');
  const handleShare = () => {
    const url = `${window.location.origin}${window.location.pathname}#test=${detail.code}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard.', 'success');
    } else {
      showToast(url, 'info');
    }
  };

  // Specs fully-spelled list (top three to match the screenshot).
  const specialtyList = (detail.specs || []).slice(0, 3).join(', ');

  return (
    <Stack spacing={2.5}>
      {/* Back link */}
      <Box>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: MP.tealDark,
            fontFamily: MONO,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            p: 0,
            '&:hover': { bgcolor: 'transparent', color: MP.dark },
          }}
        >
          Back to results
        </Button>
      </Box>

      {/* Crumbs */}
      <Box
        sx={{
          fontFamily: MONO,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: MP.textMuted,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
        }}
      >
        <Box component="span">Home</Box>
        <KeyboardArrowRightIcon sx={{ fontSize: 14, color: MP.border }} />
        <Box component="span">{sectionLabel}</Box>
        <KeyboardArrowRightIcon sx={{ fontSize: 14, color: MP.border }} />
        <Box component="span">{detail.dept}</Box>
        <KeyboardArrowRightIcon sx={{ fontSize: 14, color: MP.border }} />
        <Box component="span" sx={{ color: MP.dark }}>{detail.name}</Box>
      </Box>

      {/* Two-column grid: main + aside */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' },
          gap: { xs: 3, lg: 4 },
          alignItems: 'start',
        }}
      >
        {/* ============ MAIN COLUMN ============ */}
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {/* Hero block */}
          <Box>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: MP.tealDark,
                mb: 1,
              }}
            >
              {detail.dept}
              {specialtyList && <span> · {specialtyList}</span>}
            </Typography>

            <Typography
              sx={{
                fontFamily: DISPLAY,
                fontSize: { xs: 28, sm: 36, md: 44 },
                fontWeight: 700,
                color: MP.dark,
                letterSpacing: '0.5px',
                lineHeight: 1.05,
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              {detail.name}
            </Typography>

            {detail.aliases?.length > 0 && (
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontStyle: 'italic',
                  color: MP.textMuted,
                  mb: 2,
                }}
              >
                Also known as: {detail.aliases.join(' · ')}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <HeroPill label="Code" value={detail.code} />
              <HeroPill label="Dept" value={detail.dept} />
              <HeroPill label="" value={detail.accreditation} tone="nabl" />
            </Stack>
          </Box>

          {/* Subhead */}
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: MP.dark,
              borderBottom: `2px solid ${MP.dark}`,
              pb: 1,
              mt: 1,
            }}
          >
            Test Information
          </Typography>

          {/* Four info cards in 2x2 grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            {/* Card 1: Test Specifications */}
            <InfoCard title="Test Specifications">
              <InfoRow label="Test Name">{detail.name}</InfoRow>
              <InfoRow label="Test Code">
                <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>{detail.code}</Box>
              </InfoRow>
              <InfoRow label="Test Type">{detail.testType}</InfoRow>
              <InfoRow label="Methodology">{detail.methodology}</InfoRow>
              <InfoRow label="Department">{detail.dept}</InfoRow>
              <InfoRow label="Disease / Condition">{detail.disease}</InfoRow>
              <InfoRow label="Doctor Speciality" last>
                {(detail.specs || []).join(', ')}
              </InfoRow>
            </InfoCard>

            {/* Card 2: Test Applicability */}
            <InfoCard title="Test Applicability">
              <InfoRow label="Gender">
                <GenderRow gender={detail.gender} />
              </InfoRow>
              <InfoRow label="Age Range">
                <AgeRangeChip ageRange={detail.ageRange} />
              </InfoRow>
              <InfoRow label="Home Collection"><YesNoBadge value={detail.homeCollection} /></InfoRow>
              <InfoRow label="Walk-in"><YesNoBadge value={detail.walkIn} /></InfoRow>
              <InfoRow label="Fasting Required"><YesNoBadge value={detail.fastingRequired} /></InfoRow>
              <InfoRow label="Clinical History"><YesNoBadge value={detail.clinicalHistory} /></InfoRow>
              <InfoRow label="Consent Form"><YesNoBadge value={detail.consentForm} /></InfoRow>
              <InfoRow label="Accreditation" last>
                <AccreditationBadge label={detail.accreditation} />
              </InfoRow>
            </InfoCard>

            {/* Card 3: Sample Specifications */}
            <InfoCard title="Sample Specifications">
              <InfoRow label="Sample Type">{detail.sampleType}</InfoRow>
              <InfoRow label="Sample Volume">{detail.sampleVolume}</InfoRow>
              <InfoRow label="Sample Container">{detail.sampleContainer}</InfoRow>
              <InfoRow label="Ship Temperature">{detail.shipTemp}</InfoRow>
              <InfoRow label="Store Temperature">{detail.storeTemp}</InfoRow>
              <InfoRow label="Stability">{detail.stability}</InfoRow>
              <InfoRow label="Sample Retention" last>{detail.sampleRetention}</InfoRow>
            </InfoCard>

            {/* Card 4: Test Schedule */}
            <InfoCard title="Test Schedule">
              <InfoRow label="Test Schedule">{detail.schedule}</InfoRow>
              <InfoRow label="Sample Cutoff">
                <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>{detail.cutoff}</Box>
              </InfoRow>
              <InfoRow label="Report TAT">
                <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>{detail.tat}</Box>
              </InfoRow>
              <InfoRow label="Report Format">{detail.reportFormat}</InfoRow>
              <InfoRow label="Sample Report">
                <Box component="span" sx={{ fontStyle: 'italic', color: MP.textMuted }}>On request</Box>
              </InfoRow>
              <InfoRow label="Pre-analytical" last>
                <Box component="span" sx={{ fontSize: 12.5, color: MP.text }}>{detail.preAnalytical}</Box>
              </InfoRow>
            </InfoCard>
          </Box>

          {/* ======================================================
              Additional Information
              Parameters chip-row, sample rejection criteria, clinical
              significance copy, special instructions. Long-form metadata
              that doesn't fit the dense two-column card layout.
              ====================================================== */}
          <Box sx={{ mt: 1, bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: '6px', overflow: 'hidden', boxShadow: MP.shadow }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{
                bgcolor: MP.purple,
                color: MP.white,
                px: 2.25,
                py: 1.5,
                fontFamily: MONO,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
              }}
            >
              <Box component="span">Additional Information</Box>
              <Box component="span" sx={{ fontSize: 10, fontWeight: 700, opacity: 0.75 }}>
                {detail.code} · v1.0
              </Box>
            </Stack>

            {/* Parameters reported */}
            {detail.parameters && detail.parameters.length > 0 && (
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${MP.border}` }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: MP.textMuted,
                    }}
                  >
                    Parameters Reported
                  </Typography>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: MP.dark,
                      color: MP.white,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: MONO,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {detail.parameters.length}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                  {detail.parameters.map((p) => (
                    <Box
                      key={p}
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        bgcolor: MP.dark,
                        color: MP.white,
                        fontFamily: MONO,
                        fontSize: 11.5,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '6px',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {p}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Sample rejection criteria */}
            {detail.rejectionCriteria && detail.rejectionCriteria.length > 0 && (
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${MP.border}` }}>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: MP.textMuted,
                    mb: 1,
                  }}
                >
                  Sample Rejection Criteria
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    columnGap: 3,
                    rowGap: 0.5,
                  }}
                >
                  {detail.rejectionCriteria.map((r) => (
                    <Box
                      key={r}
                      component="li"
                      sx={{
                        position: 'relative',
                        pl: 2.25,
                        py: 0.5,
                        fontSize: 12.5,
                        color: MP.text,
                        '&::before': {
                          content: '"✕"',
                          position: 'absolute',
                          left: 0,
                          top: 4,
                          color: MP.red,
                          fontFamily: MONO,
                          fontWeight: 700,
                          fontSize: 11,
                        },
                      }}
                    >
                      {r}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Clinical significance */}
            {detail.clinicalSignificance && (
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${MP.border}` }}>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: MP.textMuted,
                    mb: 1,
                  }}
                >
                  Clinical Significance
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.6, maxWidth: '80ch' }}>
                  {detail.clinicalSignificance}
                </Typography>
              </Box>
            )}

            {/* Special instructions */}
            {detail.specialInstructions && (
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: MP.textMuted,
                    mb: 1,
                  }}
                >
                  Special Instructions
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.6, maxWidth: '80ch' }}>
                  {detail.specialInstructions}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ======================================================
              Clinical Indications (ICD-10)
              Chip row of clinical indications keyed off WHO ICD-10
              codes. Curated subset per TL-QMS-MDM-001 §C.
              ====================================================== */}
          {detail.icd10 && detail.icd10.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: MP.dark,
                  borderBottom: `2px solid ${MP.dark}`,
                  pb: 1,
                  mb: 1.5,
                }}
              >
                Clinical Indications (ICD-10)
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                {detail.icd10.map((i) => (
                  <Box
                    key={i.c}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.875,
                      bgcolor: MP.purpleLight,
                      border: '1px solid #C5B8E8',
                      color: MP.purple,
                      px: 1.25,
                      py: 0.625,
                      borderRadius: '6px',
                      fontSize: 12,
                    }}
                  >
                    <Box
                      component="code"
                      sx={{
                        fontFamily: MONO,
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        fontSize: 11.5,
                      }}
                    >
                      {i.c}
                    </Box>
                    <Box component="span" sx={{ color: MP.text }}>· {i.l}</Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* ======================================================
              Branch Availability
              Per-branch in-house / referral status. Drives the partner's
              decision on which branch to route the requisition to.
              ====================================================== */}
          {detail.branchAvailability && detail.branchAvailability.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: MP.dark,
                  borderBottom: `2px solid ${MP.dark}`,
                  pb: 1,
                  mb: 1.5,
                }}
              >
                Branch Availability
              </Typography>
              <Box sx={{ bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: '6px', overflow: 'hidden', boxShadow: MP.shadow }}>
                {/* Header */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 90px 90px', sm: '1.4fr 1fr 0.9fr 0.9fr 1fr' },
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    bgcolor: MP.bg,
                    borderBottom: `1px solid ${MP.border}`,
                    fontFamily: MONO,
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: MP.textMuted,
                  }}
                >
                  <Box>Branch</Box>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>City</Box>
                  <Box>Status</Box>
                  <Box>Local TAT</Box>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Branch Type</Box>
                </Box>
                {/* Rows */}
                {detail.branchAvailability.map((b, i) => {
                  const tone =
                    b.status === 'in-house' ? { dot: MP.green,    label: 'In-house',    color: MP.green }
                    : b.status === 'in-clinic' ? { dot: MP.tealDark, label: 'In-clinic',   color: MP.tealDark }
                    : b.status === 'referral' ? { dot: MP.orange,  label: 'Referral → NRL', color: MP.orange }
                    : { dot: MP.textMuted, label: 'Not offered', color: MP.textMuted };
                  return (
                    <Box
                      key={b.branch}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 90px 90px', sm: '1.4fr 1fr 0.9fr 0.9fr 1fr' },
                        gap: 1.5,
                        alignItems: 'center',
                        px: 2,
                        py: 1.125,
                        borderBottom: i === detail.branchAvailability.length - 1 ? 'none' : `1px solid ${MP.border}`,
                        fontSize: 12.5,
                        opacity: b.status === 'not-offered' ? 0.6 : 1,
                      }}
                    >
                      <Box sx={{ fontWeight: 600, color: MP.dark }}>{b.branch}</Box>
                      <Box sx={{ display: { xs: 'none', sm: 'block' }, color: MP.text }}>{b.city}</Box>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.dot, flexShrink: 0 }} />
                        <Box
                          component="span"
                          sx={{
                            fontFamily: MONO,
                            fontSize: 10.5,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: tone.color,
                          }}
                        >
                          {tone.label}
                        </Box>
                      </Stack>
                      <Box sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.dark }}>{b.localTat}</Box>
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          fontFamily: MONO,
                          fontSize: 10.5,
                          color: MP.textMuted,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {b.type}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Related tests (same dept) */}
          {related.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: MP.dark,
                  borderBottom: `2px solid ${MP.dark}`,
                  pb: 1,
                  mb: 1.5,
                }}
              >
                Related in {detail.dept}
              </Typography>
              <Stack spacing={1}>
                {related.map((rt) => (
                  <Box
                    key={rt.code}
                    onClick={() => onSelectTest && onSelectTest(rt)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '110px 1fr 80px 90px',
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      bgcolor: MP.white,
                      border: `1px solid ${MP.border}`,
                      borderRadius: '6px',
                      boxShadow: MP.shadow,
                      cursor: onSelectTest ? 'pointer' : 'default',
                      transition: 'all 0.14s',
                      alignItems: 'center',
                      '&:hover': onSelectTest ? { borderColor: MP.teal, bgcolor: MP.tealLight } : undefined,
                    }}
                  >
                    <Typography sx={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, color: MP.tealDark }}>
                      {rt.code}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: MP.dark }}>
                      {rt.name}
                    </Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MP.dark, textAlign: 'center' }}>
                      {rt.tat}
                    </Typography>
                    <Typography sx={{ fontFamily: DISPLAY, fontSize: 14, fontWeight: 700, color: MP.dark, textAlign: 'right' }}>
                      ₹{rt.mrp}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>

        {/* ============ ASIDE (sticky on lg) ============ */}
        <Box
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 84 },
            alignSelf: 'flex-start',
            bgcolor: MP.white,
            border: `1px solid ${MP.border}`,
            borderRadius: '6px',
            boxShadow: MP.shadow,
            overflow: 'hidden',
          }}
        >
          {/* Aside header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              bgcolor: MP.dark,
              color: MP.white,
              px: 2.25,
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
              }}
            >
              Pricing
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.04em',
                color: MP.tealMid,
              }}
            >
              {detail.code}
            </Typography>
          </Stack>

          {/* MRP-only price block. The L2L rate + savings strip have
              been removed from the portal — partner-tier pricing
              isn't surfaced on the public catalogue. `l2l` and
              `savings` still get computed upstream for any downstream
              quote / export flow; we just don't render them here. */}
          <Box sx={{ bgcolor: MP.tealLight, p: 2.25, textAlign: 'center', borderBottom: `1px solid ${MP.border}` }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                bgcolor: MP.dark,
                color: MP.teal,
                fontFamily: MONO,
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                px: 1,
                py: 0.375,
                borderRadius: '6px',
                mb: 1.25,
              }}
            >
              MRP
            </Box>
            <Typography
              sx={{
                fontFamily: DISPLAY,
                fontSize: 44,
                fontWeight: 700,
                color: MP.tealDark,
                lineHeight: 1,
                letterSpacing: '.01em',
              }}
            >
              ₹{detail.mrp.toLocaleString('en-IN')}
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: MP.tealDark,
                mt: 0.75,
              }}
            >
              Rack rate · Inclusive of GST
            </Typography>
          </Box>

          {/* Stat rows */}
          <Box>
            <AsideStat label="TAT" value={detail.tat} />
            <AsideStat label="Schedule" value={detail.schedule} />
            <AsideStat label="Sample" value={detail.sampleType} />
            <AsideStat label="Fasting" value={detail.fastingRequired ? 'Yes' : 'No'} last />
          </Box>

          {/* CTAs */}
          <Stack spacing={1} sx={{ p: 1.75, borderTop: `1px solid ${MP.border}`, bgcolor: MP.bg }}>
            <Button
              fullWidth
              onClick={handlePrint}
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: MP.teal,
                color: MP.white,
                fontWeight: 800,
                fontSize: 12.5,
                letterSpacing: '0.04em',
                textTransform: 'none',
                borderRadius: '6px',
                py: 0.875,
                '&:hover': { bgcolor: '#13C7AD' },
              }}
            >
              Print Test Card
            </Button>
            <Button
              fullWidth
              onClick={handleDownloadTrf}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: MP.white,
                color: MP.dark,
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: '0.04em',
                textTransform: 'none',
                border: `1px solid ${MP.border}`,
                borderRadius: '6px',
                py: 0.75,
                '&:hover': { bgcolor: MP.tealLight, borderColor: MP.teal },
              }}
            >
              Download TRF
            </Button>
            <Button
              fullWidth
              onClick={handleShare}
              startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: MP.white,
                color: MP.textMuted,
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: '0.04em',
                textTransform: 'none',
                border: `1px solid ${MP.border}`,
                borderRadius: '6px',
                py: 0.75,
                '&:hover': { bgcolor: MP.bg, color: MP.dark },
              }}
            >
              Share Link
            </Button>
          </Stack>

          {/* Sample report card */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              p: 1.75,
              borderTop: `1px solid ${MP.border}`,
              bgcolor: MP.white,
              opacity: detail.sampleReport ? 1 : 0.55,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 44,
                bgcolor: MP.bg,
                border: `1px solid ${MP.border}`,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 20, color: MP.textMuted }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: MP.textMuted,
                }}
              >
                Reference
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: MP.dark, lineHeight: 1.2 }}>
                Sample Report
              </Typography>
              <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.25 }}>
                {detail.sampleReport ? 'Click to download' : 'Not yet uploaded by lab'}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: 700,
                color: MP.textMuted,
                letterSpacing: '0.04em',
              }}
            >
              {detail.sampleReport ? 'PDF' : 'N/A'}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
};

export default EdosTestDetail;
